import { useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material";
import { ChevronRight, Sparkles, X } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import {
  NEW_FEATURE_CALLOUT_BADGE,
  NEW_FEATURE_CALLOUT_CLOSE_ARIA,
  NEW_FEATURE_CALLOUT_DEFAULT_CTA,
} from "../../../i18n/keysTranslations";
import Button from "../../../components/Buttons/Button";
import {
  getAnchorLayerZIndex,
  getCalloutContainer,
} from "../utils/anchorLayer";

const CALLOUT_BORDER = "#a79cff66";
const CALLOUT_BORDER_RADIUS = 15;
const CALLOUT_SHADOW = "0 10px 22px rgba(16, 16, 32, 0.27)";
const ARROW_SIZE = 10;
const ARROW_OFFSET = 5;
const CONTENT_PADDING_Y = "14px";
const CONTENT_PADDING_X = "12px";
const ARROW_SIDE_EXTRA_PADDING = "4px";

/**
 * @param {"right" | "left" | "top" | "bottom"} placement
 * @returns {object}
 */
const getArrowSxByPlacement = (placement) => {
  switch (placement) {
    case "right":
      return {
        left: -ARROW_OFFSET,
        top: "50%",
        borderLeft: `1px solid ${CALLOUT_BORDER}`,
        borderBottom: `1px solid ${CALLOUT_BORDER}`,
        transform: "translateY(-50%) rotate(45deg)",
      };
    case "left":
      return {
        right: -ARROW_OFFSET,
        top: "50%",
        borderRight: `1px solid ${CALLOUT_BORDER}`,
        borderTop: `1px solid ${CALLOUT_BORDER}`,
        transform: "translateY(-50%) rotate(45deg)",
      };
    case "bottom":
      return {
        top: -ARROW_OFFSET,
        left: "50%",
        borderLeft: `1px solid ${CALLOUT_BORDER}`,
        borderTop: `1px solid ${CALLOUT_BORDER}`,
        transform: "translateX(-50%) rotate(45deg)",
      };
    case "top":
      return {
        bottom: -ARROW_OFFSET,
        left: "50%",
        borderRight: `1px solid ${CALLOUT_BORDER}`,
        borderBottom: `1px solid ${CALLOUT_BORDER}`,
        transform: "translateX(-50%) rotate(45deg)",
      };
    default: {
      const _exhaustiveCheck = placement;
      void _exhaustiveCheck;
      return getArrowSxByPlacement("right");
    }
  }
};

/**
 * @param {"right" | "left" | "top" | "bottom"} placement
 * @returns {object}
 */
const getContentPaddingByPlacement = (placement) => {
  const basePadding = {
    py: CONTENT_PADDING_Y,
    px: CONTENT_PADDING_X,
  };

  switch (placement) {
    case "right":
      return {
        ...basePadding,
        pl: `calc(${CONTENT_PADDING_X} + ${ARROW_SIDE_EXTRA_PADDING})`,
      };
    case "left":
      return {
        ...basePadding,
        pr: `calc(${CONTENT_PADDING_X} + ${ARROW_SIDE_EXTRA_PADDING})`,
      };
    case "bottom":
      return {
        ...basePadding,
        pt: `calc(${CONTENT_PADDING_Y} + ${ARROW_SIDE_EXTRA_PADDING})`,
      };
    case "top":
      return {
        ...basePadding,
        pb: `calc(${CONTENT_PADDING_Y} + ${ARROW_SIDE_EXTRA_PADDING})`,
      };
    default: {
      const _exhaustiveCheck = placement;
      void _exhaustiveCheck;
      return basePadding;
    }
  }
};

const NewFeatureCallout = ({
  feature,
  anchorEl,
  open,
  placement: placementProp,
  onDismiss,
  onDiscover,
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const placement = placementProp ?? (isCompact ? "bottom" : "right");
  const [layerZIndex, setLayerZIndex] = useState(undefined);
  const popperRef = useRef(null);

  const primaryBackground = theme.palette.button.primary.main;
  const primaryText = theme.palette.button.primary.contrastText;

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setLayerZIndex(undefined);
      return undefined;
    }

    const syncLayerZIndex = () => {
      setLayerZIndex(getAnchorLayerZIndex(anchorEl));
    };

    syncLayerZIndex();
    window.addEventListener("resize", syncLayerZIndex);
    window.addEventListener("scroll", syncLayerZIndex, true);

    return () => {
      window.removeEventListener("resize", syncLayerZIndex);
      window.removeEventListener("scroll", syncLayerZIndex, true);
    };
  }, [anchorEl, open]);

  // Popper only repositions on scroll/resize. When the anchor changes size
  // (e.g. the slide menu expanding), follow it on every resize notification.
  useLayoutEffect(() => {
    if (!open || !anchorEl || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      popperRef.current?.update();
    });
    observer.observe(anchorEl);

    return () => observer.disconnect();
  }, [anchorEl, open]);

  if (!open || !feature || !anchorEl || typeof document === "undefined") {
    return null;
  }

  const ctaKey = feature.ctaKey || NEW_FEATURE_CALLOUT_DEFAULT_CTA;
  const calloutContainer = getCalloutContainer(anchorEl);
  const contentPadding = getContentPaddingByPlacement(placement);
  const arrowSx = getArrowSxByPlacement(placement);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      popperRef={popperRef}
      placement={placement}
      container={calloutContainer}
      sx={layerZIndex != null ? { zIndex: layerZIndex } : undefined}
      modifiers={[
        { name: "offset", options: { offset: [0, 12] } },
        { name: "preventOverflow", options: { padding: 12 } },
        {
          name: "flip",
          options: {
            fallbackPlacements: isCompact
              ? ["top", "right", "left"]
              : ["left", "bottom", "top"],
          },
        },
      ]}
    >
      <Box
        onClick={(event) => {
          // Portal events bubble through the React tree; stop them so a
          // menu Link wrapping the anchor does not navigate on callout clicks.
          event.stopPropagation();
          event.preventDefault();
        }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        sx={{
          position: "relative",
          width: isCompact ? "auto" : 220,
          maxWidth: isCompact ? "calc(100vw - 36px)" : 220,
          animation: "newFeatureCalloutEnter 0.45s ease-out",
          "@keyframes newFeatureCalloutEnter": {
            from: {
              opacity: 0,
              transform: "translateY(8px) scale(0.96)",
            },
            to: {
              opacity: 1,
              transform: "translateY(0) scale(1)",
            },
          },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            width: ARROW_SIZE,
            height: ARROW_SIZE,
            background: primaryBackground,
            borderRadius: "1px",
            zIndex: 0,
            ...arrowSx,
          }}
        />

        <Box
          role="tooltip"
          aria-label={t(feature.titleKey)}
          sx={{
            position: "relative",
            zIndex: 1,
            color: primaryText,
            border: `1px solid ${CALLOUT_BORDER}`,
            borderRadius: `${CALLOUT_BORDER_RADIUS}px`,
            background: primaryBackground,
            boxShadow: CALLOUT_SHADOW,
            ...contentPadding,
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label={t(NEW_FEATURE_CALLOUT_CLOSE_ARIA)}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onDismiss?.(event);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 2,
              display: "grid",
              placeItems: "center",
              width: 32,
              height: 32,
              minWidth: 32,
              minHeight: 32,
              p: 0,
              m: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: primaryText,
              lineHeight: 0,
              pointerEvents: "auto",
              "& svg": {
                pointerEvents: "none",
              },
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                borderRadius: "50%",
              },
            }}
          >
            <X size={16} stroke={primaryText} color={primaryText} />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              color: primaryText,
              opacity: 0.9,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.8px",
              pr: 4,
              pointerEvents: "none",
            }}
          >
            <Sparkles size={13} />
            <Box component="span" sx={{ mt: 0.2 }}>
              {t(NEW_FEATURE_CALLOUT_BADGE)}
            </Box>
          </Box>

          <Typography
            component="h2"
            sx={{
              m: "8px 0 6px",
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.25,
              pr: 4,
              color: primaryText,
              pointerEvents: "none",
            }}
          >
            {t(feature.titleKey)}
          </Typography>

          <Typography
            sx={{
              m: 0,
              color: primaryText,
              opacity: 0.85,
              fontSize: 11,
              lineHeight: 1.4,
              pr: 1,
            }}
          >
            {t(feature.descriptionKey)}
          </Typography>

          <Button
            size="small"
            color="secondary"
            variant="contained"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onDiscover?.(event);
            }}
            endIcon={<ChevronRight size={15} />}
            sx={{
              mt: "12px",
              minWidth: 0,
              height: "auto",
              px: "10px",
              py: "6px",
              borderRadius: "16px",
              fontSize: 11,
              fontWeight: 600,
              boxShadow: "none",
            }}
          >
            {t(ctaKey)}
          </Button>
        </Box>
      </Box>
    </Popper>
  );
};

export default NewFeatureCallout;
